import { useEffect, useRef } from 'react';
import { useAuth } from '@/providers/AuthProvider';

// Type augmentation for Window interface
declare global {
  interface Window {
    __originalGetDisplayMedia?: typeof MediaDevices.prototype.getDisplayMedia;
    __extensionCheckInterval?: NodeJS.Timeout;
    __isDownloadingMedia?: boolean; // Flag to exempt download errors from screenshot detection
    __isOpeningImage?: boolean; // Flag to exempt image viewing from screenshot detection
  }
  interface Navigator {
    keyboard?: {
      lock?: (keyCodes?: string[]) => Promise<void>;
      unlock?: () => void;
    };
  }
}

interface ContentProtectionOptions {
  preventScreenshot?: boolean;
  preventCopy?: boolean;
  preventRightClick?: boolean;
  preventTextSelection?: boolean;
  preventPrint?: boolean;
  preventDevTools?: boolean;
  watermark?: boolean;
  preventScreenCapture?: boolean; // NEW: Prevent getDisplayMedia
  preventKeyboardLock?: boolean; // NEW: Lock keyboard keys
  blurOnInactive?: boolean; // NEW: Blur when tab is inactive
}

/**
 * Hook to protect sensitive content from screenshots and copying
 *
 * WARNING: These are deterrents only. Determined users can still bypass these protections.
 * For true security, sensitive data should be handled server-side.
 */
export function useContentProtection(options: ContentProtectionOptions = {}) {
  const {
    preventScreenshot = true,
    preventCopy = true,
    preventRightClick = true,
    preventTextSelection = true,
    preventPrint = true,
    preventDevTools = false,
    watermark = false,
    preventScreenCapture = true, // NEW
    preventKeyboardLock = true, // NEW
    blurOnInactive = true, // NEW
  } = options;

  const isProtected = useRef(false);
  const { logout } = useAuth(); // Get logout function from AuthProvider
  const permissionCheckInterval = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    if (isProtected.current) return;
    isProtected.current = true;

    // Check if we're on the assessment perform page
    const isAssessmentPage = window.location.pathname.includes('/assessments/perform/');
    
    // Detect iOS devices (iOS doesn't prompt for clipboard permissions)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    // CRITICAL: Check clipboard permission and block access if denied
    const checkClipboardPermission = async () => {
      try {
        // Try to query clipboard permission
        const permissionStatus = await navigator.permissions.query({ 
          name: 'clipboard-read' as PermissionName 
        });
        
        if (permissionStatus.state === 'denied') {
          console.error('🚫 Clipboard permission DENIED - User cannot access this page');
          showPermissionBlockedUI();
          return false;
        } else if (permissionStatus.state === 'prompt') {
          console.warn('⚠️ Clipboard permission not granted yet - Requesting permission');
          // Try to request permission by attempting to read clipboard
          try {
            await navigator.clipboard.read();
            console.warn('✅ Clipboard permission GRANTED');
            removePermissionBlockedUI();
            return true;
          } catch (_err) {
            console.error('🚫 Clipboard permission DENIED by user');
            showPermissionBlockedUI();
            return false;
          }
        } else if (permissionStatus.state === 'granted') {
          console.warn('✅ Clipboard permission already GRANTED');
          removePermissionBlockedUI();
          return true;
        }
      } catch (_err) {
        // Fallback: Try to read clipboard directly
        try {
          await navigator.clipboard.read();
          console.warn('✅ Clipboard access granted (fallback check)');
          removePermissionBlockedUI();
          return true;
        } catch (_clipErr) {
          console.error('🚫 Clipboard access DENIED (fallback check)');
          showPermissionBlockedUI();
          return false;
        }
      }
      return false;
    };

    // Show blocking UI when permission is denied
    const showPermissionBlockedUI = () => {
      // Remove any existing blocking UI first
      removePermissionBlockedUI();
      
      // Get current language from localStorage
      const currentLang = localStorage.getItem('i18nextLng') || 'en';
      const isRTL = currentLang === 'ar';
      
      // Detect if mobile device
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Load translations with mobile-specific instructions
      const translations = {
        en: {
          title: 'Clipboard Permission Required',
          description: 'To protect the security of this assessment, you must allow clipboard access. This helps us prevent unauthorized screenshots and copying of sensitive content.',
          howToEnable: 'How to enable:',
          step1: isMobileDevice 
            ? '1. Tap the 🔒 lock/info icon in your browser\'s address bar (or browser menu ⋮)' 
            : '1. Click the 🔒 lock icon in your browser\'s address bar',
          step2: '2. Find "Clipboard" or "Site permissions"',
          step3: '3. Change it from "Block" to "Allow"',
          step4: isMobileDevice 
            ? '4. Reload the page (pull down to refresh)' 
            : '4. Refresh this page',
          tryAgain: 'Try Again',
          exitAssessment: 'Exit Assessment',
          stillBlocked: '❌ Clipboard permission is still blocked. Please enable it in your browser settings.'
        },
        ar: {
          title: 'إذن الحافظة مطلوب',
          description: 'لحماية أمان هذا التقييم، يجب عليك السماح بالوصول إلى الحافظة. يساعدنا هذا في منع لقطات الشاشة غير المصرح بها ونسخ المحتوى الحساس.',
          howToEnable: 'كيفية التفعيل:',
          step1: isMobileDevice
            ? '1. اضغط على أيقونة القفل/المعلومات 🔒 في شريط عنوان المتصفح (أو قائمة المتصفح ⋮)'
            : '1. انقر على أيقونة القفل 🔒 في شريط عنوان المتصفح',
          step2: '2. ابحث عن "الحافظة" أو "أذونات الموقع"',
          step3: '3. غيّرها من "حظر" إلى "السماح"',
          step4: isMobileDevice
            ? '4. أعد تحميل الصفحة (اسحب للأسفل للتحديث)'
            : '4. قم بتحديث هذه الصفحة',
          tryAgain: 'حاول مرة أخرى',
          exitAssessment: 'الخروج من التقييم',
          stillBlocked: '❌ إذن الحافظة لا يزال محظورًا. يرجى تفعيله في إعدادات المتصفح.'
        }
      };
      
      const t = translations[currentLang as 'en' | 'ar'] || translations.en;
      
      const blockDiv = document.createElement('div');
      blockDiv.id = 'clipboard-permission-blocked';
      blockDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.98);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: all;
        direction: ${isRTL ? 'rtl' : 'ltr'};
      `;
      blockDiv.innerHTML = `
        <div style="text-align: center; color: white; font-family: Arial, sans-serif; padding: ${isMobileDevice ? '20px' : '40px'}; max-width: ${isMobileDevice ? '90%' : '600px'};">
          <div style="font-size: ${isMobileDevice ? '60px' : '80px'}; margin-bottom: ${isMobileDevice ? '20px' : '30px'};">🔒</div>
          <div style="font-size: ${isMobileDevice ? '24px' : '32px'}; font-weight: bold; margin-bottom: 20px;">${t.title}</div>
          <div style="font-size: ${isMobileDevice ? '16px' : '18px'}; color: #ff6b6b; line-height: 1.6; margin-bottom: ${isMobileDevice ? '20px' : '30px'};">
            ${t.description}
          </div>
          <div style="font-size: ${isMobileDevice ? '14px' : '16px'}; color: #999; line-height: 1.6; margin-bottom: ${isMobileDevice ? '20px' : '30px'}; text-align: ${isRTL ? 'right' : 'left'};">
            <strong style="color: white;">${t.howToEnable}</strong><br>
            ${t.step1}<br>
            ${t.step2}<br>
            ${t.step3}<br>
            ${t.step4}
          </div>
          <div style="display: flex; flex-direction: ${isMobileDevice ? 'column' : 'row'}; gap: ${isMobileDevice ? '10px' : '0'}; justify-content: center; align-items: center;">
            <button id="retry-permission-btn" style="
              background: #4CAF50;
              color: white;
              border: none;
              padding: ${isMobileDevice ? '12px 30px' : '15px 40px'};
              font-size: ${isMobileDevice ? '16px' : '18px'};
              border-radius: 8px;
              cursor: pointer;
              margin-${isMobileDevice ? '0' : (isRTL ? 'left' : 'right')}: ${isMobileDevice ? '0' : '15px'};
              font-weight: bold;
              width: ${isMobileDevice ? '100%' : 'auto'};
              touch-action: manipulation;
            ">${t.tryAgain}</button>
            <button id="exit-page-btn" style="
              background: #f44336;
              color: white;
              border: none;
              padding: ${isMobileDevice ? '12px 30px' : '15px 40px'};
              font-size: ${isMobileDevice ? '16px' : '18px'};
              border-radius: 8px;
              cursor: pointer;
              font-weight: bold;
              width: ${isMobileDevice ? '100%' : 'auto'};
              touch-action: manipulation;
            ">${t.exitAssessment}</button>
          </div>
        </div>
      `;
      document.body.appendChild(blockDiv);
      
      // Add button handlers
      const retryBtn = document.getElementById('retry-permission-btn');
      const exitBtn = document.getElementById('exit-page-btn');
      
      if (retryBtn) {
        retryBtn.onclick = async () => {
          const granted = await checkClipboardPermission();
          if (!granted) {
            alert(t.stillBlocked);
          }
        };
      }
      
      if (exitBtn) {
        exitBtn.onclick = () => {
          // Redirect to home page or assessments list
          window.location.href = '/assessments';
        };
      }
    };

    // Remove blocking UI
    const removePermissionBlockedUI = () => {
      const blockDiv = document.getElementById('clipboard-permission-blocked');
      if (blockDiv) {
        blockDiv.remove();
      }
    };

    // Only enforce clipboard permission on assessment perform page
    // SKIP iOS: iOS Safari/Chrome don't prompt for clipboard permissions
    if (isAssessmentPage && !isIOS) {
      console.warn('🔍 Assessment page detected - Checking clipboard permission...');
      
      // Initial check
      checkClipboardPermission();
      
      // Re-check every 5 seconds to catch permission changes
      permissionCheckInterval.current = setInterval(async () => {
        await checkClipboardPermission();
      }, 5000);
    } else if (isAssessmentPage && isIOS) {
      console.warn('🔍 Assessment page detected - iOS device, skipping clipboard permission check (iOS doesn\'t prompt)');
    }

    const handlers: Array<{
      event: string;
      handler: (e: Event) => void;
      target?: EventTarget;
    }> = [];

    // Prevent context menu (right-click)
    if (preventRightClick) {
      const contextMenuHandler = (e: Event) => {
        e.preventDefault();
        return false;
      };
      document.addEventListener('contextmenu', contextMenuHandler);
      handlers.push({ event: 'contextmenu', handler: contextMenuHandler });
    }

    // Prevent text selection
    if (preventTextSelection) {
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      const bodyStyle = document.body.style as unknown as Record<
        string,
        string
      >;
      bodyStyle.msUserSelect = 'none';
      bodyStyle.mozUserSelect = 'none';
    }

    // Prevent copy (Ctrl+C, Cmd+C)
    if (preventCopy) {
      const copyHandler = (e: Event) => {
        e.preventDefault();
        return false;
      };
      document.addEventListener('copy', copyHandler);
      document.addEventListener('cut', copyHandler);
      handlers.push({ event: 'copy', handler: copyHandler });
      handlers.push({ event: 'cut', handler: copyHandler });
    }

    // ============================================
    // MOBILE SCREENSHOT DETECTION - MULTI-SIGNAL APPROACH
    // ============================================
    // Browser limitation: Cannot directly detect hardware button combinations
    // Solution: Use multiple indirect signals and combine them with confidence scoring
    
    // Mobile detection utilities
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Function to trigger immediate logout with reason
    const triggerImmediateLogout = (reason: string) => {
      console.error(`🚫 SCREENSHOT DETECTED - ${reason}`);
      
      // Get current language for translations
      const currentLang = localStorage.getItem('i18nextLng') || 'en';
      const isRTL = currentLang === 'ar';
      
      // Translations for screenshot detection
      const translations = {
        en: {
          title: 'SCREENSHOT DETECTED',
          loggingOut: 'Logging out for security...',
          alertMessage: '⛔ SCREENSHOT DETECTED: Screenshot image detected. You will be logged out immediately for security reasons.'
        },
        ar: {
          title: 'تم اكتشاف لقطة شاشة',
          loggingOut: 'جاري تسجيل الخروج للأمان...',
          alertMessage: '⛔ تم اكتشاف لقطة شاشة: تم اكتشاف صورة لقطة شاشة. سيتم تسجيل خروجك فورًا لأسباب أمنية.'
        }
      };
      
      const t = translations[currentLang as 'en' | 'ar'] || translations.en;
      
      // Block the entire UI immediately
      const blockDiv = document.createElement('div');
      blockDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.98);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: all;
        direction: ${isRTL ? 'rtl' : 'ltr'};
      `;
      blockDiv.innerHTML = `
        <div style="text-align: center; color: white; font-family: Arial, sans-serif; padding: 40px;">
          <div style="font-size: 80px; margin-bottom: 30px;">🚫</div>
          <div style="font-size: 32px; font-weight: bold; margin-bottom: 20px;">${t.title}</div>
          <div style="font-size: 18px; color: #ff6b6b;">${reason}</div>
          <div style="font-size: 16px; margin-top: 30px; color: #999;">${t.loggingOut}</div>
        </div>
      `;
      document.body.appendChild(blockDiv);
      
      // Show alert with translated message
      alert(t.alertMessage);
      
      // Force logout after alert is dismissed
      console.warn('🔴 Calling logout() after screenshot detection');
      setTimeout(() => {
        logout();
      }, 100); // Small delay to ensure alert is dismissed and logout executes
    };

    // ============================================
    // MOBILE SCREENSHOT PREVENTION
    // ============================================
    
    let clipboardCheckInterval: NodeJS.Timeout | undefined;
    let permissionStatusWatcher: PermissionStatus | undefined;
    
    if (isMobile && preventScreenshot) {
      // HYBRID APPROACH: Event-driven + Strategic Polling
      // 1. Continuous polling triggers OS-level blocking on Honor/Xiaomi/Huawei
      // 2. Clipboard check on visibility change catches screenshots taken when tab was hidden
      // 3. Permission API monitors clipboard access attempts
      
      // Track last clipboard contents to detect NEW images only
      let lastClipboardHash = '';
      
      // Function to check clipboard for screenshot images
      const checkClipboardForScreenshot = async () => {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            if (item.types.some(type => type.startsWith('image/'))) {
              // Create a simple hash to detect if this is the SAME image
              const currentHash = item.types.join(',');
              if (currentHash !== lastClipboardHash) {
                lastClipboardHash = currentHash;
                triggerImmediateLogout('Mobile: Screenshot detected');
                return true;
              }
            }
          }
        } catch (_err) {
          // Silent - permission denied or no clipboard access
        }
        return false;
      };
      
      // STRATEGY 1: Continuous polling (triggers OS-level blocking + detects screenshots)
      clipboardCheckInterval = setInterval(async () => {
        await checkClipboardForScreenshot();
      }, 50);
      
      // STRATEGY 2: Listen for Permissions API changes (detects clipboard access attempts)
      if ('permissions' in navigator && 'query' in navigator.permissions) {
        navigator.permissions.query({ name: 'clipboard-read' as PermissionName })
          .then((permStatus) => {
            permissionStatusWatcher = permStatus;
            
            // Listen for permission state changes
            permStatus.addEventListener('change', async () => {
              // Permission changed - check clipboard immediately
              await checkClipboardForScreenshot();
            });
          })
          .catch(() => {
            // Permissions API not supported or failed
          });
      }
      
      // STRATEGY 3: Aggressive visibility change detection
      const mobileVisibilityHandler = async () => {
        if (!document.hidden) {
          // Tab became visible - check clipboard immediately
          // This catches screenshots taken while tab was hidden
          await checkClipboardForScreenshot();
        }
      };
      
      document.addEventListener('visibilitychange', mobileVisibilityHandler);
      handlers.push({
        event: 'visibilitychange',
        handler: mobileVisibilityHandler as (e: Event) => void,
        target: document,
      });
      
      // STRATEGY 4: Page resume detection (mobile-specific)
      const pageShowHandler = async () => {
        // Page was restored from bfcache or resumed
        await checkClipboardForScreenshot();
      };
      
      window.addEventListener('pageshow', pageShowHandler);
      handlers.push({
        event: 'pageshow',
        handler: pageShowHandler as (e: Event) => void,
        target: window,
      });
    }
    
    // ============================================
    // DESKTOP SCREENSHOT DETECTION
    // ============================================
    
    if (!isMobile && preventScreenshot) {
      // Track keys for Win+Shift+S detection with multiple strategies
      const pressedKeys = new Set<string>();
      let windowsKeyPressed = false;
      let shiftKeyPressed = false;
      let metaKeyPressed = false;

      // Create watermark overlay for Windows key detection
      const createWatermarkOverlay = () => {
        // Remove blur from body first
        document.body.style.filter = 'none';
        
        let overlay = document.getElementById('windows-key-watermark');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.id = 'windows-key-watermark';
          overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 0, 0, 0.5);
            z-index: 99999;
            pointer-events: none;
            display: flex;
            align-items: center;
            justify-content: center;
          `;
          
          const textContainer = document.createElement('div');
          textContainer.style.cssText = `
            font-size: 72px;
            font-weight: bold;
            color: white;
            text-shadow: 4px 4px 10px rgba(0, 0, 0, 1);
            background: rgba(0, 0, 0, 0.95);
            padding: 40px 80px;
            border-radius: 30px;
            border: 4px solid white;
            text-align: center;
            line-height: 1.4;
            position: relative;
            z-index: 100000;
          `;
          textContainer.innerHTML = '⚠️ SCREENSHOT BLOCKED ⚠️<br><span style="font-size: 32px; margin-top: 20px; display: block; color: #ffff00;">Press ESC to close</span>';
          
          overlay.appendChild(textContainer);
          document.body.appendChild(overlay);
        }
        overlay.style.display = 'flex';
        
        // Blur all child elements except the overlay
        const allElements = document.body.children;
        for (let i = 0; i < allElements.length; i++) {
          const elem = allElements[i] as HTMLElement;
          if (elem.id !== 'windows-key-watermark') {
            elem.style.filter = 'blur(10px)';
          }
        }
      };

      const removeWatermarkOverlay = () => {
        const overlay = document.getElementById('windows-key-watermark');
        if (overlay) {
          overlay.style.display = 'none';
        }
        // Remove blur from all elements
        const allElements = document.body.children;
        for (let i = 0; i < allElements.length; i++) {
          const elem = allElements[i] as HTMLElement;
          elem.style.filter = 'none';
        }
        document.body.style.filter = 'none';
      };

      // CAPTURE PHASE - This runs BEFORE the target element
      const keydownHandlerCapture = (e: KeyboardEvent) => {

        // Track meta/windows key state IMMEDIATELY
        if (e.key === 'Meta' || e.key === 'OS' || e.metaKey) {
          metaKeyPressed = true;
          windowsKeyPressed = true;
        }
        if (e.key === 'Shift' || e.shiftKey) {
          shiftKeyPressed = true;
        }

        // IMMEDIATE blocking for Win+Shift+S (highest priority)
        const isWinKey = metaKeyPressed || windowsKeyPressed || e.metaKey;
        const isShift = shiftKeyPressed || e.shiftKey;
        const isSKey = e.key === 's' || e.key === 'S' || e.code === 'KeyS';

        if (isWinKey && isShift && isSKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          alert('⛔ Windows Snipping Tool is blocked!');
          console.error('🚫🚫🚫 Win+Shift+S BLOCKED IN CAPTURE PHASE');
          return false;
        }

        // Block PrintScreen immediately - AGGRESSIVE (check multiple properties)
        const isPrintScreen = 
          e.key === 'PrintScreen' || 
          e.code === 'PrintScreen' || 
          e.keyCode === 44 || 
          e.which === 44 ||
          e.key === 'Print' ||
          e.code === 'Print';

        if (isPrintScreen) {
          console.error('🚫🚫🚫 PrintScreen DETECTED IN CAPTURE:', e.key, e.code, e.keyCode);
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          
          // Clear clipboard immediately
          navigator.clipboard.writeText('🔒 Screenshots are blocked').catch(() => {});
          
          alert('⛔ Screenshots are blocked!');
          return false;
        }
      };

      // Desktop: CONTINUOUS smart clipboard monitoring with paste detection
      // Track when user intentionally pastes (Ctrl+V) to avoid false positives
      let lastClipboardCheck = 0;
      let lastClipboardImageTime = 0;
      let userInitiatedPaste = false;
      let clipboardMonitorInterval: NodeJS.Timeout | undefined;
      
      // Start continuous but smart clipboard monitoring
      const startContinuousClipboardMonitoring = () => {
        if (clipboardMonitorInterval) return;
        
        console.warn('🔍 Desktop: Continuous clipboard monitoring active (checks every 500ms)');
        
        clipboardMonitorInterval = setInterval(async () => {
          const now = Date.now();
          if (now - lastClipboardCheck < 500) return; // Throttle to max 500ms
          lastClipboardCheck = now;
          
          try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
              if (item.types.some(type => type.startsWith('image/'))) {
                // Image found in clipboard
                const imageTime = now;
                
                // Check if this is a NEW image (not the same one we saw before)
                if (imageTime - lastClipboardImageTime > 1000) {
                  // New image detected
                  
                  // Check if user just pasted (Ctrl+V within last 2 seconds)
                  if (userInitiatedPaste && (now - lastClipboardImageTime < 2000)) {
                    console.warn('🔍 Desktop: Image paste detected (Ctrl+V) - NOT a screenshot, ignoring');
                    userInitiatedPaste = false;
                  } else {
                    // This is a NEW screenshot (not from paste)
                    console.error('🚫 Desktop: Screenshot detected in clipboard!');
                    triggerImmediateLogout('Desktop: Screenshot image detected in clipboard');
                    // Stop monitoring after detection
                    if (clipboardMonitorInterval) {
                      clearInterval(clipboardMonitorInterval);
                      clipboardMonitorInterval = undefined;
                    }
                  }
                  
                  lastClipboardImageTime = imageTime;
                }
                break;
              }
            }
          } catch (_err) {
            // Clipboard access denied - expected without permission
          }
        }, 500); // Check every 500ms
      };
      
      // Start monitoring immediately
      startContinuousClipboardMonitoring();

      const keydownHandler = (e: KeyboardEvent) => {
        // ESC key to close the overlay
        if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
          removeWatermarkOverlay();
          windowsKeyPressed = false;
          shiftKeyPressed = false;
          metaKeyPressed = false;
          pressedKeys.clear();
          return;
        }
        
        // DO NOT trigger watermark on Shift alone - only track state
        if (e.key === 'Shift') {
          shiftKeyPressed = true;
        }
        
        // DO NOT trigger watermark on Windows key alone - only track state
        if (e.key === 'Meta' || e.key === 'OS' || e.metaKey) {
          windowsKeyPressed = true;
          metaKeyPressed = true;
        }
        
        // Track Ctrl+V (paste) to mark as user-initiated
        if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V' || e.code === 'KeyV')) {
          userInitiatedPaste = true;
          console.warn('🔍 Desktop: Ctrl+V paste detected - marking next clipboard image as paste (not screenshot)');
        }

        // Track pressed keys for combination detection
        pressedKeys.add(e.key);
        pressedKeys.add(e.code);
        if (e.metaKey) pressedKeys.add('Meta');
        if (e.ctrlKey) pressedKeys.add('Control');
        if (e.shiftKey) {
          pressedKeys.add('Shift');
          shiftKeyPressed = true;
        }
        if (e.altKey) pressedKeys.add('Alt');

        let shouldBlock = false;

        // Check for Win/Meta key first
        const hasWinKey =
          windowsKeyPressed || pressedKeys.has('Meta') || pressedKeys.has('OS') || e.metaKey;
        const hasShiftKey = pressedKeys.has('Shift') || e.shiftKey;
        const hasSKey =
          pressedKeys.has('S') ||
          pressedKeys.has('s') ||
          pressedKeys.has('KeyS') ||
          e.key === 'S' ||
          e.key === 's' ||
          e.code === 'KeyS';

        // CRITICAL: Block Win+Shift+S IMMEDIATELY (Windows Snipping Tool)
        if (hasWinKey && hasShiftKey && hasSKey) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay(); // Show watermark for screenshot combo
          console.error('🚫🚫🚫 CRITICAL: Win+Shift+S BLOCKED (Windows Snipping Tool)');
          alert('⛔ Screenshot tool is disabled on this page for security reasons!');
          return false;
        }

        // CRITICAL: Block Win+PrtScn FIRST (Windows - saves to Pictures/Screenshots)
        if (hasWinKey && (e.key === 'PrintScreen' || e.keyCode === 44 || e.code === 'PrintScreen')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫🚫🚫 CRITICAL: Win+PrtScn BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // CRITICAL: Block Alt+PrtScn (Windows - active window)
        if (e.altKey && (e.key === 'PrintScreen' || e.keyCode === 44 || e.code === 'PrintScreen')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫🚫🚫 CRITICAL: Alt+PrtScn BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // CRITICAL: Block Win+G (Windows Game Bar)
        if (hasWinKey && (e.key === 'g' || e.key === 'G' || e.code === 'KeyG')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫🚫🚫 CRITICAL: Win+G BLOCKED (Game Bar)');
          alert('⛔ Screenshot tool is disabled on this page for security reasons!');
          return false;
        }

        // CRITICAL: Block Fn+Windows+Spacebar (Surface devices)
        if (hasWinKey && e.code === 'Space') {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫🚫🚫 CRITICAL: Win+Space BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Windows: PrtScn alone (full screen to clipboard)
        if (
          e.key === 'PrintScreen' ||
          e.keyCode === 44 ||
          e.code === 'PrintScreen'
        ) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          // Clear clipboard to prevent screenshot
          navigator.clipboard
            .writeText('🔒 Screenshot blocked for security reasons')
            .catch(() => {});
          console.warn('🚫 Screenshot attempt blocked - PrintScreen key');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Mac: Cmd+Shift+3 (full screen)
        if (e.metaKey && e.shiftKey && e.key === '3') {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Mac Cmd+Shift+3 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Mac: Cmd+Shift+4 (select area)
        if (e.metaKey && e.shiftKey && e.key === '4') {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Mac Cmd+Shift+4 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Mac: Cmd+Shift+5 (screenshot toolbar)
        if (e.metaKey && e.shiftKey && e.key === '5') {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Mac Cmd+Shift+5 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Mac: Cmd+Shift+6 (Touch Bar screenshot)
        if (e.metaKey && e.shiftKey && e.key === '6') {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Mac Cmd+Shift+6 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Chrome OS: Ctrl + Show Windows key (Ctrl + F5)
        if (e.ctrlKey && (e.key === 'F5' || e.code === 'F5')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Chrome OS Ctrl+F5 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Chrome OS: Ctrl+Shift+F5 (partial screenshot)
        if (e.ctrlKey && e.shiftKey && (e.key === 'F5' || e.code === 'F5')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Chrome OS Ctrl+Shift+F5 BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Linux: Shift+PrtScn (select area)
        if (e.shiftKey && (e.key === 'PrintScreen' || e.keyCode === 44 || e.code === 'PrintScreen')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Linux Shift+PrtScn BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Linux: Ctrl+PrtScn (screenshot to clipboard)
        if (e.ctrlKey && (e.key === 'PrintScreen' || e.keyCode === 44 || e.code === 'PrintScreen')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Linux Ctrl+PrtScn BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Linux/KDE: Meta+PrtScn
        if (e.metaKey && (e.key === 'PrintScreen' || e.keyCode === 44 || e.code === 'PrintScreen')) {
          shouldBlock = true;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          createWatermarkOverlay();
          console.error('🚫 Linux Meta+PrtScn BLOCKED');
          alert('⛔ Screenshot is disabled on this page for security reasons!');
          return false;
        }

        // Additional detection: Any time S is pressed with Shift and any modifier
        if (
          e.shiftKey &&
          (e.key === 'S' || e.key === 's' || e.code === 'KeyS')
        ) {
          if (e.ctrlKey || e.metaKey || e.altKey) {
            shouldBlock = true;
            createWatermarkOverlay();
            console.warn('🚫 Screenshot attempt blocked - Modifier+Shift+S');
          }
        }

        // Additional Windows shortcuts: Alt+PrtScn
        if (
          e.altKey &&
          (e.key === 'PrintScreen' || e.keyCode === 44)
        ) {
          shouldBlock = true;
          console.warn('🚫 Screenshot attempt blocked - Alt+PrtScn');
        }

        // Prevent Ctrl+P (Print)
        if (
          preventPrint &&
          (e.ctrlKey || e.metaKey) &&
          (e.key === 'p' || e.key === 'P' || e.code === 'KeyP')
        ) {
          shouldBlock = true;
          console.warn('🚫 Print attempt blocked');
        }

        if (shouldBlock) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      };

      const keyupHandler = (e: KeyboardEvent) => {
        // Track Shift key release (no watermark removal since it's not shown on Shift alone)
        if (e.key === 'Shift') {
          shiftKeyPressed = false;
        }
        
        // Track Windows key release (no watermark removal since it's not shown on Win alone)
        if (e.key === 'Meta' || e.key === 'OS') {
          windowsKeyPressed = false;
          metaKeyPressed = false;
        }

        // Clear pressed keys on keyup
        pressedKeys.delete(e.key);
        pressedKeys.delete(e.code);
        if (!e.metaKey) {
          pressedKeys.delete('Meta');
          windowsKeyPressed = false;
          metaKeyPressed = false;
          removeWatermarkOverlay();
        }
        if (!e.ctrlKey) pressedKeys.delete('Control');
        if (!e.shiftKey) {
          pressedKeys.delete('Shift');
          shiftKeyPressed = false;
        }
        if (!e.altKey) pressedKeys.delete('Alt');
      };

      // CRITICAL: Add CAPTURE PHASE listener first (runs before bubble phase)
      document.addEventListener('keydown', keydownHandlerCapture, true);
      window.addEventListener('keydown', keydownHandlerCapture, true);
      
      // Add normal listeners in CAPTURE phase (highest priority)
      document.addEventListener('keydown', keydownHandler, true);
      document.addEventListener('keyup', keyupHandler, true);
      document.addEventListener('keypress', keydownHandler, true);
      window.addEventListener('keydown', keydownHandler, true);
      window.addEventListener('keyup', keyupHandler, true);

      handlers.push({
        event: 'keydown',
        handler: keydownHandlerCapture as (e: Event) => void,
        target: document,
      });
      handlers.push({
        event: 'keydown',
        handler: keydownHandlerCapture as (e: Event) => void,
        target: window,
      });
      handlers.push({
        event: 'keydown',
        handler: keydownHandler as (e: Event) => void,
        target: document,
      });
      handlers.push({
        event: 'keyup',
        handler: keyupHandler as (e: Event) => void,
        target: document,
      });
      handlers.push({
        event: 'keypress',
        handler: keydownHandler as (e: Event) => void,
        target: document,
      });
      handlers.push({
        event: 'keydown',
        handler: keydownHandler as (e: Event) => void,
        target: window,
      });
      handlers.push({
        event: 'keyup',
        handler: keyupHandler as (e: Event) => void,
        target: window,
      });
    }

    // Prevent drag and drop
    if (preventCopy) {
      const dragHandler = (e: Event) => {
        e.preventDefault();
        return false;
      };
      document.addEventListener('dragstart', dragHandler);
      handlers.push({ event: 'dragstart', handler: dragHandler });
    }

    // Detect when user leaves the page (might be taking screenshot)
    if (preventScreenshot) {
      // REMOVED automatic blur on window focus/blur to prevent popup issues
      // Protection overlay only appears for DevTools or visibility changes

      // Mobile screenshot detection already handled above (clipboard monitoring)

      // Clear clipboard when window gets focus (catches PrintScreen taken while unfocused)
      const focusHandler = async () => {
        try {
          await navigator.clipboard.writeText('🔒 Screenshots are blocked');
          console.warn('🔒 Clipboard cleared on focus');
        } catch (_err) {
          // Clipboard access denied
        }
      };
      
      window.addEventListener('focus', focusHandler);
      handlers.push({ event: 'focus', handler: focusHandler, target: window });

      // Only detect page visibility change (tab switch, minimize)
      const visibilityHandler = () => {
        if (document.hidden) {
          console.warn('⚠️ Page hidden - content protected');
          
          // Blur content when tab is inactive (NEW)
          if (blurOnInactive) {
            document.body.style.filter = 'blur(10px)';
          }
        } else {
          // Remove blur when tab becomes active again
          if (blurOnInactive) {
            document.body.style.filter = 'none';
          }
          
          // Clear clipboard when page becomes visible
          navigator.clipboard.writeText('🔒 Screenshots are blocked').catch(() => {});
        }
      };

      // Add all event listeners
      // ONLY visibilitychange, NOT window blur/focus to avoid popup conflicts
      document.addEventListener('visibilitychange', visibilityHandler, true);

      handlers.push({
        event: 'visibilitychange',
        handler: visibilityHandler,
        target: document,
      });
    }

    // Prevent printing
    if (preventPrint) {
      const printHandler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      window.addEventListener('beforeprint', printHandler);
      window.addEventListener('afterprint', printHandler);
      handlers.push({
        event: 'beforeprint',
        handler: printHandler,
        target: window,
      });
      handlers.push({
        event: 'afterprint',
        handler: printHandler,
        target: window,
      });

      // Add CSS to hide content when printing
      const style = document.createElement('style');
      style.id = 'print-protection';
      style.textContent = `
        @media print {
          body * {
            display: none !important;
          }
          body::before {
            content: "This content cannot be printed for security reasons.";
            display: block !important;
            text-align: center;
            padding: 50px;
            font-size: 20px;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // NEW: Prevent Screen Capture API (getDisplayMedia)
    if (preventScreenCapture) {
      // Override getDisplayMedia to prevent screen recording/capture
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
        
        navigator.mediaDevices.getDisplayMedia = function() {
          console.warn('🚫 Screen capture blocked by content protection');
          return Promise.reject(new DOMException('Screen capture is not allowed on this page', 'NotAllowedError'));
        };
        
        // Store original for cleanup
        window.__originalGetDisplayMedia = originalGetDisplayMedia;
      }
    }

    // NEW: Lock specific keyboard keys using Keyboard Lock API
    if (preventKeyboardLock && navigator.keyboard) {
      // Lock common screenshot keys
      const keysToLock = [
        'PrintScreen',
        'KeyS', // For Win+Shift+S, Cmd+Shift+S
        'KeyP', // For print
      ];
      
      navigator.keyboard.lock?.(keysToLock).then(() => {
        console.warn('🔒 Keyboard screenshot keys locked');
      }).catch((e: Error) => {
        console.warn('⚠️ Keyboard lock not available:', e.message);
      });
    }

    // NEW: Detect browser extensions that might bypass protection
    if (preventScreenshot) {
      // Check for common screenshot extension indicators
      const checkExtensions = () => {
        // Check if certain global variables exist that screenshot extensions might inject
        const suspiciousGlobals = ['__SCREENSHOT_EXTENSION__', '__NIMBUS__', '__LIGHTSHOT__'];
        const detected = suspiciousGlobals.some(name => name in window);
        
        if (detected) {
          console.error('⚠️ Screenshot extension detected!');
          // Could trigger additional security measures here
        }
      };
      
      checkExtensions();
      // Check periodically
      const extensionCheckInterval = setInterval(checkExtensions, 5000);
      
      // Cleanup function will clear this interval
      window.__extensionCheckInterval = extensionCheckInterval;
    }

    // Add watermark overlay
    if (watermark) {
      const watermarkDiv = document.createElement('div');
      watermarkDiv.id = 'content-watermark';
      watermarkDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 200px,
          rgba(0, 0, 0, 0.03) 200px,
          rgba(0, 0, 0, 0.03) 250px
        );
      `;

      const watermarkText = document.createElement('div');
      watermarkText.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-45deg);
        font-size: 80px;
        color: rgba(0, 0, 0, 0.05);
        white-space: nowrap;
        user-select: none;
        pointer-events: none;
      `;
      watermarkText.textContent = 'CONFIDENTIAL';
      watermarkDiv.appendChild(watermarkText);
      document.body.appendChild(watermarkDiv);
    }

    // Log security event
    console.warn('🔒 Content protection enabled for sensitive data');

    // Cleanup function
    return () => {
      isProtected.current = false;

      // Remove all event listeners (including capture phase)
      handlers.forEach(({ event, handler, target }) => {
        const eventTarget = target || document;
        // Remove both bubble and capture phase
        eventTarget.removeEventListener(event, handler, false);
        eventTarget.removeEventListener(event, handler, true);
      });

      // Restore text selection
      if (preventTextSelection) {
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
        const bodyStyle = document.body.style as unknown as Record<
          string,
          string
        >;
        bodyStyle.msUserSelect = '';
        bodyStyle.mozUserSelect = '';
      }

      // Restore blur filter
      if (preventScreenshot) {
        document.body.style.filter = 'none';

        // Remove protection overlay if it exists
        const overlay = document.getElementById(
          'screenshot-protection-overlay'
        );
        if (overlay) {
          overlay.remove();
        }

        // Remove Windows key watermark overlay
        const watermarkOverlay = document.getElementById('windows-key-watermark');
        if (watermarkOverlay) {
          watermarkOverlay.remove();
        }
      }

      // Remove print protection style
      if (preventPrint) {
        const style = document.getElementById('print-protection');
        if (style) style.remove();
      }

      // Remove watermark
      if (watermark) {
        const watermarkDiv = document.getElementById('content-watermark');
        if (watermarkDiv) watermarkDiv.remove();
      }

      // NEW: Restore getDisplayMedia
      if (preventScreenCapture && window.__originalGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = window.__originalGetDisplayMedia;
        delete window.__originalGetDisplayMedia;
      }

      // NEW: Unlock keyboard
      if (preventKeyboardLock && navigator.keyboard) {
        navigator.keyboard.unlock?.();
      }

      // Clear clipboard monitoring interval
      if (preventScreenshot && clipboardCheckInterval) {
        clearInterval(clipboardCheckInterval);
        clipboardCheckInterval = undefined;
      }

      // Remove permission status watcher
      if (permissionStatusWatcher) {
        // Remove all event listeners from permission status
        permissionStatusWatcher = undefined;
      }

      // Clear extension check interval
      if (window.__extensionCheckInterval) {
        clearInterval(window.__extensionCheckInterval);
        delete window.__extensionCheckInterval;
      }

      // Clear permission check interval
      if (permissionCheckInterval.current) {
        clearInterval(permissionCheckInterval.current);
        permissionCheckInterval.current = undefined;
      }

      // Remove permission blocked UI
      const blockDiv = document.getElementById('clipboard-permission-blocked');
      if (blockDiv) {
        blockDiv.remove();
      }

      console.warn('🔓 Content protection disabled');
    };
  }, [
    preventScreenshot,
    preventCopy,
    preventRightClick,
    preventTextSelection,
    preventPrint,
    preventDevTools,
    watermark,
    preventScreenCapture,
    preventKeyboardLock,
    blurOnInactive,
    logout,
  ]);

  return {
    isProtected: isProtected.current,
  };
}
